import React, { useState, useCallback, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import { useHistory } from "react-router-dom";
import UploadBox from "../../../../shared/molecules/fields/UploadBox";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import {
  GET_PRESIGNED_URL,
  GET_ALL_EXAM_PAPERS,
} from "../../../../utils/graphql/services/ai-evaluation/queries";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_EXAM_PAPER_ANSWER_KEY } from "../../../../utils/graphql/services/ai-evaluation/mutations";
import { Alert, Snackbar } from "@mui/material";

type CreateExamAnswerKeyInput = {
  examPaperId: string | number;
  answerKeyPublicPdfUrl: string;
  mode: string;
};

const DEFAULT_INPUT: CreateExamAnswerKeyInput = {
  examPaperId: "",
  answerKeyPublicPdfUrl: "",
  mode: "",
};

type ExamPaper = { id: string | number; title: string };

function CreateExamAnswerKeyPage() {
  const history = useHistory();
  const [form, setForm] = useState<CreateExamAnswerKeyInput>(DEFAULT_INPUT);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openError, setOpenError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<ExamPaper[]>([]);

  const [createAnswerKey, { loading: creating }] = useMutation(
    CREATE_EXAM_PAPER_ANSWER_KEY,
    {
      client: autoGradeClient,
    }
  );

  const isValid =
    String(form.examPaperId).trim() !== "" &&
    form.answerKeyPublicPdfUrl.trim() !== "" &&
    form.mode.trim() !== "";

  const handleChange = (field: keyof CreateExamAnswerKeyInput, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      refetch({
        input: {
          limit: 10,
          offset: 0,
          title: searchValue || undefined,
          createdFrom: null,
          createdTill: null,
          updatedFrom: null,
          updatedTill: null,
        },
      });
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const { data, loading, error, refetch } = useQuery(GET_ALL_EXAM_PAPERS, {
    variables: {
      input: {
        limit: 10,
        offset: 0,
        title: undefined,
        createdFrom: null,
        createdTill: null,
        updatedFrom: null,
        updatedTill: null,
      },
    },
    client: autoGradeClient,
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (data?.getExamPapers?.examPapers) {
      setFilteredOptions(data.getExamPapers.examPapers);
    } else {
      setFilteredOptions([]);
    }
  }, [data]);

  const handleSearchChange = useCallback(
    (event: any, newInputValue: string) => {
      setSearchValue(newInputValue);
    },
    []
  );

  const handleExamPaperChange = useCallback(
    (event: any, newValue: ExamPaper | null) => {
      handleChange("examPaperId", newValue ? newValue.id : "");
    },
    []
  );

  const getSelectedExamPaper = useCallback(() => {
    if (!form.examPaperId || !filteredOptions.length) return null;
    return filteredOptions.find((ep) => ep.id === form.examPaperId) || null;
  }, [form.examPaperId, filteredOptions]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.examPaperId) {
      setErrorMessage("Please select an exam paper");
      setOpenError(true);
      return;
    }

    if (!form.answerKeyPublicPdfUrl.trim()) {
      setErrorMessage("Please upload an answer key PDF");
      setOpenError(true);
      return;
    }

    if (!form.mode.trim()) {
      setErrorMessage("Please select a grading mode");
      setOpenError(true);
      return;
    }

    try {
      await createAnswerKey({
        variables: { input: form },
      });
      setSuccessMessage("Answer key created successfully!");
      setOpenSuccess(true);

      setTimeout(() => {
        history.push("/ai-evaluation/answer-key");
      }, 2000);
    } catch (error: any) {
      console.error("Failed to create answer key:", error);
      setErrorMessage(
        error?.message || "Failed to create answer key. Please try again."
      );
      setOpenError(true);
    }
  };

  return (
    <Grid container spacing={3} sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box mb={3}>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              Create Answer Key
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload and configure an answer key for an exam paper
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSave}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={getSelectedExamPaper()}
                  onChange={handleExamPaperChange}
                  onInputChange={handleSearchChange}
                  options={filteredOptions}
                  getOptionLabel={(option) => option?.title || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  loading={loading}
                  noOptionsText={
                    loading ? "Loading..." : "No exam papers found"
                  }
                  filterOptions={(options) => options}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Exam Paper"
                      placeholder="Search exam papers by title..."
                      required
                      error={!form.examPaperId && Boolean(errorMessage)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <Box
                                component="span"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mr: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mr: 1 }}
                                >
                                  Loading...
                                </Typography>
                              </Box>
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body1">{option.title}</Typography>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  required
                  error={!form.mode && Boolean(errorMessage)}
                >
                  <InputLabel id="mode-select-label">Grading Mode</InputLabel>
                  <Select
                    labelId="mode-select-label"
                    value={form.mode}
                    label="Grading Mode"
                    onChange={(e) => handleChange("mode", e.target.value)}
                  >
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Lenient">Lenient</MenuItem>
                    <MenuItem value="Strict">Strict</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ mt: 2, mb: 2, fontWeight: 500 }}
                >
                  Answer Key Document
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 2 }}>
                      <UploadBox
                        setUserAnswerAsPdf={(url: string) =>
                          handleChange("answerKeyPublicPdfUrl", url)
                        }
                        filename={uploadedFileName}
                        setFilename={setUploadedFileName}
                        usecase="answersheet_upload"
                        acceptedFormats={["pdf"]}
                        presignedUrlQuery={GET_PRESIGNED_URL}
                        client={autoGradeClient}
                        placeholderText="Click to choose answer key PDF"
                      />
                    </Box>
                    <TextField
                      fullWidth
                      label="Answer Key PDF URL"
                      value={form.answerKeyPublicPdfUrl}
                      onChange={(e) =>
                        handleChange("answerKeyPublicPdfUrl", e.target.value)
                      }
                      placeholder="PDF URL will appear here after upload"
                      InputProps={{
                        readOnly: !form.answerKeyPublicPdfUrl,
                      }}
                      helperText="Upload a PDF file or enter a direct URL to the answer key"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                        height: "fit-content",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        Upload Guidelines
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="ul"
                        sx={{ pl: 0, listStyle: "none" }}
                      >
                        <li>• Only PDF files are accepted</li>
                        <li>• Maximum file size: 10MB</li>
                        <li>• Ensure answer key is clear and readable</li>
                        <li>• Include all sections and questions</li>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => history.goBack()}
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid || creating}
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    {creating ? "Creating..." : "Create Answer Key"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
      <Snackbar
        open={openError}
        autoHideDuration={6000}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenError(false)}
          severity="error"
          variant="filled"
          sx={{
            width: "100%",
            minWidth: "300px",
            "& .MuiAlert-message": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={openSuccess}
        autoHideDuration={6000}
        onClose={() => setOpenSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSuccess(false)}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            minWidth: "300px",
            "& .MuiAlert-message": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default CreateExamAnswerKeyPage;
